import { useLang } from "@/lib/lang";
import { deleteConversation } from "@/lib/api";
import { invalidateConversations } from "@/lib/cache/invalidate";
import { useConversations } from "@/hooks/queries";
import { Button } from "@/components/ui/button";
import { History, Plus, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const EMPTY_DOC_IDS = [];

function formatRelativeTime(iso, lang) {
  if (!iso) return "";
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return lang === "ar" ? "الآن" : "now";
  if (diffMins < 60) return lang === "ar" ? `${diffMins} د` : `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return lang === "ar" ? `${diffHours} س` : `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return lang === "ar" ? `${diffDays} ي` : `${diffDays}d`;
}

export default function ConversationHistoryPane({
  chatContextDocIds = EMPTY_DOC_IDS,
  caseId = null,
  researchSessionId = null,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
}) {
  const { t, lang } = useLang();

  const documentIds =
    !caseId && !researchSessionId && chatContextDocIds.length > 0
      ? chatContextDocIds
      : undefined;

  const { data: conversations = [], isLoading: loading, isFetching } = useConversations({
    caseId,
    researchSessionId,
    documentIds,
    includeMessages: false,
  });

  const handleDelete = async (e, convId) => {
    e.stopPropagation();
    if (!window.confirm(t("delete_conversation"))) return;
    try {
      await deleteConversation(convId);
      toast.success(t("conversation_deleted"));
      if (activeConversationId === convId) {
        onNewConversation?.();
      }
      await invalidateConversations();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const showLoading = loading && conversations.length === 0;

  return (
    <div className="flex flex-col h-full bg-white" data-testid="conversation-history">
      <div className="grid-border-b px-4 py-3 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-2">
          <History size={14} className="text-slate-600" />
          <span className="eyebrow !text-[10px]">{t("history")}</span>
        </div>
        <Button
          data-testid="new-conversation-btn"
          variant="outline"
          size="sm"
          onClick={() => onNewConversation?.()}
          className="rounded-none h-7 px-2 text-[10px] border-slate-300 gap-1"
        >
          <Plus size={12} />
          {t("new_chat")}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {showLoading && (
          <div className="px-4 py-6 text-xs text-slate-400 text-center">{t("streaming")}</div>
        )}
        {!showLoading && conversations.length === 0 && (
          <div className="px-4 py-8 text-center">
            <MessageSquare size={20} className="mx-auto mb-2 text-slate-300" />
            <p className="text-xs text-slate-400">{t("no_conversations")}</p>
          </div>
        )}
        {conversations.map((conv) => {
          const active = conv.id === activeConversationId;
          const label = conv.preview || conv.title || t("new_chat");
          return (
            <div
              key={conv.id}
              role="button"
              tabIndex={0}
              data-testid={`conv-history-${conv.id}`}
              onClick={() => onSelectConversation?.(conv)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectConversation?.(conv);
                }
              }}
              className={[
                "w-full text-start px-4 py-3 grid-border-b transition-colors group cursor-pointer",
                active ? "bg-slate-900 text-white" : "hover:bg-slate-50",
              ].join(" ")}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className={["text-xs font-medium truncate", active ? "text-white" : "text-slate-900"].join(" ")}>
                    {label}
                  </div>
                  <div className={["text-[10px] mt-0.5", active ? "text-white/60" : "text-slate-400"].join(" ")}>
                    {formatRelativeTime(conv.updated_at, lang)}
                    {conv.message_count ? ` · ${conv.message_count}` : ""}
                    {isFetching && active ? " · …" : ""}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`conv-delete-${conv.id}`}
                  onClick={(e) => handleDelete(e, conv.id)}
                  className={[
                    "opacity-0 group-hover:opacity-100 p-1 transition-opacity",
                    active ? "text-white/70 hover:text-white" : "text-slate-400 hover:text-red-600",
                  ].join(" ")}
                  aria-label={t("delete")}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
